import { i18nNsFactory } from '@atlas-frontend/i18n';
import { isDayjs } from 'dayjs';
import translations from './DirectoryFilter.translations.json';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { Main, Padding, SidePanel, SidePanelProps, Stack } from '.';
import { Button, Form } from 'antd';
import styled from 'styled-components';
import { token } from '@atlas-frontend/design-tokens';
import { FormInstance } from 'antd/es/form/hooks/useForm';

const { addTranslations, useTranslation } = i18nNsFactory('directory-filter');
addTranslations(translations);

export interface FieldProps<T extends Record<string, any>> {
  name: keyof T;
  label: string;
  initialValue?: T[keyof T];
  input: () => React.ReactElement;
}

export interface DirectoryFilterProps<
  T extends Record<string, any> = Record<string, any>,
> extends Omit<SidePanelProps, 'children'> {
  fields?: FieldProps<T>[];
  onFilter: (values: T) => void;
  onClear: () => void;
  onClose: () => void;
  isOpen?: boolean;
}

export const DirectoryFilter = forwardRef<FormInstance, DirectoryFilterProps>(
  (props, ref) => {
    const {
      fields,
      onFilter,
      onClear,
      onClose,
      isOpen,
      collapsedWidth = 0,
      side = 'right',
      minWidth = 360,
      showToggleCollapseButton = false,
      ...rest
    } = props;
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const handleClear = useCallback(() => {
      const clearedValues =
        fields?.reduce(
          (acc, field) => {
            acc[field.name as string] = undefined;
            return acc;
          },
          {} as Record<string, any>,
        ) || {};

      form.setFieldsValue(clearedValues);
      onClear?.();
    }, [fields, form, onClear]);

    const filterFields = useMemo(
      () =>
        fields?.map(({ name, label, input }) => (
          <Form.Item name={name} label={label} key={name}>
            {input()}
          </Form.Item>
        )),
      [fields],
    );

    // Interim solution to convert dayjs instances to Date in anticipation of native Date based picker: https://ant.design/docs/react/use-custom-date-library
    const handleFinish = useCallback(
      (values: Record<string, any>) => {
        const dayjsToDate = (value: any) =>
          isDayjs(value) ? value.toDate() : value;

        onFilter(
          Object.fromEntries(
            Object.entries(values).map(([key, value]) => [
              key,
              Array.isArray(value)
                ? value.map(dayjsToDate)
                : dayjsToDate(value),
            ]),
          ),
        );
      },
      [onFilter],
    );

    const initialValues = useMemo(
      () =>
        fields?.reduce(
          (acc, field) => {
            if (field.initialValue !== undefined) {
              acc[field.name as string] = field.initialValue;
            }
            return acc;
          },
          {} as Record<string, any>,
        ),
      [fields],
    );

    return (
      <Form
        form={form}
        ref={ref}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={initialValues}
      >
        <SidePanel
          collapsedWidth={collapsedWidth}
          side={side}
          mode="layered"
          showToggleCollapseButton={showToggleCollapseButton}
          minWidth={minWidth}
          defaultCollapsed={false}
          {...rest}
        >
          <SidePanelWrapper>
            <Main.Header>
              <Stack size={'sm'} direction={'horizontal'}>
                <h2>{t('title')}</h2>
              </Stack>
            </Main.Header>

            <SidePanelContentWrapper>
              <Padding all>{filterFields}</Padding>
            </SidePanelContentWrapper>

            <Main.Footer>
              <ButtonWrapper>
                <Stack direction={'horizontal'} size={'sm'} justify={'end'}>
                  <Button id="close-directory-filter" onClick={onClose}>
                    {t('button.close')}
                  </Button>
                  <Button id="clear-directory-filter" onClick={handleClear}>
                    {t('button.clear')}
                  </Button>
                  <Button
                    id="apply-directory-filter"
                    type={'primary'}
                    htmlType={'submit'}
                  >
                    {t('button.filter')}
                  </Button>
                </Stack>
              </ButtonWrapper>
            </Main.Footer>
          </SidePanelWrapper>
        </SidePanel>
      </Form>
    );
  },
);

const SidePanelWrapper = styled.div`
  display: grid;
  grid-template-rows: auto 1fr 60px;
  height: 100%;
  width: 100%;
  overflow: hidden;
  border-left: 1px solid ${token.colorBorderSecondary};
`;

const SidePanelContentWrapper = styled.div`
  min-width: 300px;
  overflow-y: auto;
`;

const ButtonWrapper = styled.div`
  width: 100%;
`;
